"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ErrorResponse = exports.ValidateReferralResponse = exports.MeResponse = exports.ProfileResponse = exports.GenericMessageResponse = exports.AuthSuccessResponse = exports.RegisterResponse = exports.ValidateReferralQuery = exports.VerifyEmailChangeBody = exports.RequestEmailChangeBody = exports.UpdateDisplayNameBody = exports.VerifyLoginCodeBody = exports.RequestLoginCodeBody = exports.VerifyEmailBody = exports.RegisterBody = exports.VerificationCode = exports.EmailAddress = void 0;
/**
 * SOLID Review
 * S — Strong. One concern: the wire shapes of the /api/auth surface. No transport, no handlers,
 *     no business rules — a schema module and nothing else.
 * O — Strong. A new auth endpoint is a new schema pair plus one registry entry; no existing schema
 *     is touched.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Each endpoint gets its OWN request and response schema rather than one shared
 *     "auth payload" object, so a route can reach exactly the shape it serves.
 * D — Strong. Depends only on zod. It is the contract both sides depend on, so it must depend on
 *     neither of them.
 */
/**
 * DDD Layer: none — this is the shared wire contract, above both workspaces' layers.
 *
 * These are the client half of backend/src/framework/http/routes/authRoutes.ts and the server half
 * of frontend/src/infrastructure/api/authApiService.ts. Until now those two halves were written out
 * twice by hand: the routes read `req.body as { email?: string; code?: string }` — an unchecked
 * cast — and the client re-declared every response as an interface. authApiService.ts's own header
 * said so: "Nothing mechanically ties the two halves together; that is a gap worth closing."
 *
 * A Zod schema is one declaration that ties them: the route PARSES with it (so a malformed body is
 * rejected by the contract rather than by a hand-rolled `if` two fields deep) and the client INFERS
 * from it (so a response shape cannot drift without a compile error).
 */
const zod_1 = require("zod");
/**
 * Trimmed, lower-cased, and validated as an address.
 *
 * The transform is part of the CONTRACT, not a convenience: the backend looks accounts up by email,
 * so " Foo@Example.com " and "foo@example.com" must not be two accounts. Doing it here means both
 * sides normalise identically, which two hand-written implementations demonstrably would not.
 */
exports.EmailAddress = zod_1.z.string().trim().toLowerCase().pipe(zod_1.z.email())
    // ⚠ THE PUBLISHED RULE (2026-09-30). Without this the OpenAPI document said `type: string` and
    // nothing more: the generator loses `z.email()` inside a pipe, so the spec understated what the
    // server validates. `.meta` is zod's own metadata — no runtime change, nothing the consumers must
    // extend — and the generator reads it. (`z.email().trim()` would publish it too, but refuses
    // " a@b.c " instead of trimming it: a behaviour change, not a documentation fix.)
    .meta({ format: 'email', description: 'Trimmed and lower-cased, then validated as an address.' });
/** The 4-digit code mailed for verification and passwordless login (see e2e/lib/funnel.ts). */
exports.VerificationCode = zod_1.z.string().trim().regex(/^\d{4}$/, 'must be four digits');
// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------
exports.RegisterBody = zod_1.z.object({
    email: exports.EmailAddress,
    displayName: zod_1.z.string().trim().min(1).max(60),
    referralCode: zod_1.z.string().trim().max(10).optional(),
    consentAccepted: zod_1.z.boolean().optional(),
});
exports.VerifyEmailBody = zod_1.z.object({ email: exports.EmailAddress, code: exports.VerificationCode });
exports.RequestLoginCodeBody = zod_1.z.object({ email: exports.EmailAddress });
exports.VerifyLoginCodeBody = zod_1.z.object({ email: exports.EmailAddress, code: exports.VerificationCode });
exports.UpdateDisplayNameBody = zod_1.z.object({ displayName: zod_1.z.string().trim().min(1).max(60) });
exports.RequestEmailChangeBody = zod_1.z.object({ email: exports.EmailAddress });
exports.VerifyEmailChangeBody = zod_1.z.object({ email: exports.EmailAddress, code: exports.VerificationCode });
/**
 * `?code=` on GET /validate-referral.
 *
 * TRUNCATES at 10 rather than rejecting, because that is what the route does
 * (`.trim().toUpperCase().slice(0, 10)`) and a contract describes the server that exists, not the
 * server one would design. A `.max(10)` here would have been a nicer rule and a false one — the
 * endpoint would have started 400-ing input it currently accepts.
 *
 * Worth a second look separately: silently truncating means an eleven-character typo can resolve to
 * a DIFFERENT, valid ten-character code. That is a real (if small) correctness question, and
 * changing it is a behaviour change, not a contract change — so it is recorded here rather than
 * fixed in passing.
 *
 * `code` is optional because the route answers `{ valid: false }` for a missing one instead of 400.
 */
exports.ValidateReferralQuery = zod_1.z.object({
    code: zod_1.z.string().optional().default('').transform((raw) => raw.trim().toUpperCase().slice(0, 10)),
});
// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------
/**
 * The same for every address (Q11, 2026-10-21; handbook §28.249): an address with an account and one without get this
 * very body — so it carries the sentence and no player id, which would have differed (or been missing).
 */
exports.RegisterResponse = zod_1.z.object({
    message: zod_1.z.string(),
});
exports.AuthSuccessResponse = zod_1.z.object({
    jwt: zod_1.z.string(),
    playerId: zod_1.z.string(),
    displayName: zod_1.z.string(),
    matchCredits: zod_1.z.number().int(),
    emailVerified: zod_1.z.boolean(),
});
exports.GenericMessageResponse = zod_1.z.object({ message: zod_1.z.string() });
exports.ProfileResponse = zod_1.z.object({
    playerId: zod_1.z.string(),
    displayName: zod_1.z.string(),
    email: zod_1.z.string().nullable(),
    matchCredits: zod_1.z.number().int(),
    emailVerified: zod_1.z.boolean(),
});
exports.MeResponse = zod_1.z.object({
    id: zod_1.z.string(),
    displayName: zod_1.z.string(),
    email: zod_1.z.string().nullable(),
    matchCredits: zod_1.z.number().int(),
    emailVerified: zod_1.z.boolean(),
    isAdmin: zod_1.z.boolean(),
});
exports.ValidateReferralResponse = zod_1.z.object({ valid: zod_1.z.boolean() });
/** Every non-2xx body on this surface. The routes emit exactly `{ error: string }`. */
exports.ErrorResponse = zod_1.z.object({ error: zod_1.z.string() });
//# sourceMappingURL=auth.js.map